import { ChangeEventHandler, FunctionComponent } from 'react'

import styled, { css } from 'styled-components'

type Option = { label: string; value: string }

type SegmentedButtonProps = {
  options: Option[]
  value: string
  onChange: ChangeEventHandler<HTMLInputElement>
  className?: string
}

interface StyledButtonProps {
  isActive: boolean
}

const StyledWrapper = styled.div`
  ${({
    theme: {
      colors: { fillDefault, strokeStrong },
    },
  }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    box-sizing: border-box;

    background-color: ${fillDefault};
    border-radius: 80px;
    border: 1px solid ${strokeStrong};
  `}
`

const StyledItem = styled.label<StyledButtonProps>`
  ${({
    theme: {
      colors: {
        textDefault,
        fillDefault,
        textInverse,
        fillInverse,
        strokeStrong,
      },
      typography: {
        subheads: {
          small1: { bundledCSS: small1SubheadCSS },
        },
      },
    },
    isActive,
  }) => css`
    ${small1SubheadCSS};
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    flex: 1;
    cursor: pointer;

    color: ${textDefault};
    background-color: ${fillDefault};
    border-left: 1px solid ${strokeStrong};

    &:first-child {
      border-bottom-left-radius: 80px;
      border-top-left-radius: 80px;
      border-left: none;
    }

    &:last-child {
      border-bottom-right-radius: 80px;
      border-top-right-radius: 80px;
    }

    ${isActive &&
    css`
      color: ${textInverse};
      background-color: ${fillInverse};
    `}
  `}
`

const StyledInput = styled.input`
  display: none;
`

const SegmentedButton: FunctionComponent<SegmentedButtonProps> = ({
  options,
  value,
  onChange,
  className,
}) => (
  <StyledWrapper className={className} role="radiogroup">
    {options.map((option) => {
      return (
        <StyledItem key={option.value} isActive={option.value === value}>
          <StyledInput
            type="radio"
            name={option.label}
            value={option.value}
            checked={option.value === value}
            onChange={onChange}
          />
          {option.label}
        </StyledItem>
      )
    })}
  </StyledWrapper>
)

export default SegmentedButton
